import { useRef } from 'react';

import { useGame } from '../state/useGame.ts';
import { Board, type BoardHandle } from './Board.tsx';
import { EntryScreen } from './EntryScreen.tsx';
import { PlayScreen } from './PlayScreen.tsx';
import { FinishedScreen } from './FinishedScreen.tsx';

export function App(): React.ReactElement {
  const boardRef = useRef<BoardHandle | null>(null);
  const game = useGame(boardRef);

  if (game.phase === 'loading') {
    return (
      <div className="app">
        <div className="loading" role="status">
          <p className="wordmark">
            word soup<span className="dot">.</span>
          </p>
          <p>Fetching today’s puzzle…</p>
        </div>
      </div>
    );
  }

  if (game.phase === 'error' || !game.puzzle) {
    return (
      <div className="app">
        <div className="loading" role="alert">
          <p className="wordmark">
            word soup<span className="dot">.</span>
          </p>
          <p>Today’s puzzle could not be loaded.</p>
          <p className="entry__note">{game.error}</p>
          <button className="button" type="button" onClick={() => location.reload()}>
            Try again
          </button>
        </div>
      </div>
    );
  }

  if (game.phase === 'entry') {
    return (
      <div className="app">
        <EntryScreen game={game} />
      </div>
    );
  }

  // The board component is mounted for both play and the finish screen so the WebGL
  // context is not thrown away and rebuilt when a game ends.
  const board = game.state ? (
    <Board
      ref={boardRef}
      board={game.state.board}
      w={game.puzzle.w}
      h={game.puzzle.h}
      selection={game.selection}
      disabled={game.busy || game.phase !== 'playing'}
      showPath={game.mode.requiresAdjacency}
      onTileDown={game.tileDown}
      onTileEnter={game.tileEnter}
      onGestureEnd={game.gestureEnd}
      onGestureCancel={game.cancelSelection}
      onTileActivate={game.tileActivate}
    />
  ) : null;

  return (
    <div className="app">
      {game.phase === 'finished' ? (
        <FinishedScreen game={game} />
      ) : (
        <PlayScreen game={game} board={board} />
      )}
    </div>
  );
}
